import React, { useEffect, useState, useMemo } from "react";
import { Search, FileText, CreditCard, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { bookingsApi } from "../api";

const TABS = ["All", "Pending Pay", "Confirmed", "Active", "Completed", "Cancelled"];

const statusKey = (label) =>
  ({
    All: null,
    "Pending Pay": "PENDING PAYMENT",
    Confirmed: "CONFIRMED",
    Active: "ACTIVE",
    Completed: "COMPLETED",
    Cancelled: "CANCELLED",
  }[label]);

const statusBadge = (status) =>
  ({
    CONFIRMED: "badge badge-blue",
    "PENDING PAYMENT": "badge badge-amber",
    ACTIVE: "badge badge-green",
    COMPLETED: "badge badge-gray",
    CANCELLED: "badge badge-red",
  }[status] || "badge badge-gray");

export default function BookingsView() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("All");
  const [query, setQuery] = useState("");

  const refresh = async () => {
    try {
      const data = await bookingsApi.list();
      setBookings(data);
    } catch (e) {
      toast.error("Failed to load bookings", { description: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    const key = statusKey(tab);
    return bookings.filter((b) => {
      if (key && b.status !== key) return false;
      if (query) {
        const q = query.toLowerCase();
        return (
          (b.car_name || "").toLowerCase().includes(q) ||
          (b.customer || "").toLowerCase().includes(q) ||
          (b.email || "").toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [bookings, tab, query]);

  const updateStatus = async (id, status) => {
    try {
      await bookingsApi.updateStatus(id, status);
      toast.success(`Status updated to ${status}`);
      refresh();
    } catch (e) {
      toast.error("Update failed", { description: e.message });
    }
  };

  return (
    <div className="section-card">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5">
        <h2 className="text-xl font-bold">Bookings ({bookings.length})</h2>
        <div className="relative md:w-80 w-full">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            className="input-soft pl-9"
            placeholder="Search by customer / car..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      </div>

      <div className="tabs-row mb-5">
        {TABS.map((t) => (
          <button key={t} className={`chip ${tab === t ? "active" : ""}`} onClick={() => setTab(t)}>
            {t}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-400">
          <Loader2 className="animate-spin mr-2" size={18} /> Loading bookings...
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center muted py-8">No bookings found.</div>
      ) : (
        <div className="space-y-3">
          {filtered.map((b) => (
            <div key={b.id} className="card-soft p-5">
              <div className="flex items-start gap-3">
                {b.car_image ? <img src={b.car_image} alt="" className="car-thumb" /> : <div className="car-thumb" />}
                <div className="flex-1 min-w-0">
                  <div className="font-bold">{b.car_name}</div>
                  <div className="text-sm muted mt-1">
                    {b.customer} · {b.email}
                  </div>
                  <div className="text-sm muted mt-1">
                    {b.pickup_date} → {b.dropoff_date} · {b.days} days
                  </div>
                </div>
                <div className="text-right flex-shrink-0 space-y-1">
                  <div>
                    <span className={statusBadge(b.status)}>{b.status}</span>
                  </div>
                  <div>
                    <span className={`badge ${b.paid ? "badge-green" : "badge-red"} inline-flex items-center gap-1`}>
                      <CreditCard size={11} /> {b.paid ? "PAID" : "UNPAID"}
                    </span>
                  </div>
                </div>
              </div>
              <div className="subtle-divider" />
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="text-sm muted">
                  Subtotal <span className="font-bold text-gray-900">${Number(b.subtotal).toFixed(2)}</span> ·{" "}
                  Tax {b.tax_pct}% <span className="font-bold text-gray-900">${Number(b.tax).toFixed(2)}</span> ·{" "}
                  <span className="text-red-strong">Total ${Number(b.total).toFixed(2)}</span> · {b.payment_method}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    className="pill-btn blue inline-flex items-center gap-1"
                    onClick={() =>
                      toast.info("Receipt", { description: `Reservation ${b.ref} — emailed to ${b.email}` })
                    }
                  >
                    <FileText size={14} /> Receipt
                  </button>
                  <select
                    className="select-soft"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) updateStatus(b.id, e.target.value);
                    }}
                  >
                    <option value="">Change status...</option>
                    <option value="PENDING PAYMENT">Pending Payment</option>
                    <option value="CONFIRMED">Confirmed</option>
                    <option value="ACTIVE">Active</option>
                    <option value="COMPLETED">Completed</option>
                    <option value="CANCELLED">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
